import { appConfig } from 'engine/appConfig';
import { GameTypeOptions as GlobalGameTypeOptions, type GameRound, type GameType } from 'shared/modelTypes/game';

// Game type options (reimported to not break anything)
export const GameTypeOptions = GlobalGameTypeOptions.filter((x) => appConfig.gameTypes.includes(x.value as GameType));

// Languages options
export const LanguagesOptions = [
	{ label: 'Dutch', value: 'nl' },
	{ label: 'English', value: 'en' },
	{ label: 'German', value: 'de' },
	{ label: 'French', value: 'fr' },
	{ label: 'Spanish', value: 'es' },
	{ label: 'Italian', value: 'it' },
];

// Automatic navigation options
export const GameAutomaticNavigationOptions = [
	{ label: 'None', value: 'none' },
	{ label: 'Per Round', value: 'per-round' },
	{ label: 'Per Task', value: 'per-task' },
];

// // Game start options
// export const GameStartByOptions = [
// 	{ label: 'Always Available', value: 'always-available' },
// 	{ label: 'Game Master', value: 'game-master' },
// 	{ label: 'Time', value: 'time' },
// ];

// // Game end options
// export const GameEndsWhenOptions = [
// 	{ label: 'Never', value: 'never' },
// 	{ label: 'Game Master', value: 'game-master' },
// 	{ label: 'Duration', value: 'duration' },
// ];

// Teams options
export const TeamsOptions = [
	{ label: 'Default', value: 'default' },
	{ label: 'Hotseat', value: 'hotseat' },
];

// Game round availability options
export const GameRoundAvailabilityOptions = [
	{ label: 'Always', value: 'always' },
	{ label: 'Game Master', value: 'game-master' },
	{ label: 'Time (Minutes)', value: 'time-minutes' },
	{ label: 'Round Completed', value: 'round-completed' },
	appConfig.permissions.allowGps ? { label: 'GPS', value: 'gps' } : undefined,
	// { label: 'QR Code', value: 'qr-code' },
].filter((x) => x !== undefined);

export function makeGameRoundsFlowchart(rounds: GameRound[]): string {
	// these rounds are linked to other nodes (they have an unlock condition or are the unlock condition)

	const subgraphStartRoundIds = rounds.filter((r) => r.availability === 'round-completed').map((r) => r.availabilityRoundToComplete);
	const linkedRoundIds = [
		//
		...rounds.filter((r) => r.availability === 'round-completed').map((r) => r.id),
		...subgraphStartRoundIds,
	];
	const subgraphStartRounds = rounds.filter((r) => subgraphStartRoundIds.includes(r.id));
	const linkedRounds = rounds.filter((r) => linkedRoundIds.includes(r.id));
	const unlinkedRounds = rounds.filter((r) => !linkedRoundIds.includes(r.id));

	const step2Rounds = unlinkedRounds.length ? [unlinkedRounds.at(-1)!, ...subgraphStartRounds] : subgraphStartRounds;

	// cspell:disable
	return `digraph {
	rankdir=TB;
	fontname="system-ui"
	node [fontname="system-ui", shape=rect, style="rounded", penwidth=1, margin="0.3,0.15"]
	edge [fontname="system-ui"]

	${unlinkedRounds
		.map((r) => {
			const subtitle = r.availability === 'round-completed' ? '' : `(${r.availability})`;
			const label = subtitle ? `${r.title}\\n${subtitle}` : r.title;
			return `"${r.id}" [label="${label}"];`;
		})
		.join('\n')}

	${
		// step 1:
		// invisible arrows connecting unlinked nodes so they will render at the top and top-to-bottom
		unlinkedRounds.length < 2 ? '' : unlinkedRounds.map((r) => `"${r.id}"`).join(' -> ') + '[style=invis];'
	}

	${
		// step 2:
		// connecting the last unlinked node and subgraph start nodes so they also render top to bottom
		step2Rounds.length < 2 ? '' : step2Rounds.map((r) => `"${r.id}"`).join(' -> ') + '[style=invis];'
	}

	${linkedRounds
		.map((r) => {
			const subtitle = r.availability === 'round-completed' ? '' : `(${r.availability})`;
			const label = subtitle ? `${r.title}\\n${subtitle}` : r.title;
			return `"${r.id}" [label="${label}"];`;
		})
		.join('\n')}


	${rounds
		.filter((r) => r.availability === 'round-completed' && r.availabilityRoundToComplete)
		.map((r) => `"${r.availabilityRoundToComplete}" -> "${r.id}";`)
		.join('\n')}

}`;
	// cspell:enable
}

// GameTaskTypes Options
/**
 * GameTaskTypeOptions filtered based on platform
 */

export const GameTaskTypeOptions = [
	{ label: 'Validation by Game Master', value: 'validation-by-game-master' },
	{ label: 'Automatic Validation', value: 'automatic-validation' },
	{ label: 'Information', value: 'information' },
	{ label: 'Fullscreen Video', value: 'fullscreen-video' },
	appConfig.permissions.allowAr ? { label: 'Augmented Reality', value: 'augmented-reality' } : undefined,
	appConfig.permissions.allowBingo ? { label: 'Bingo', value: 'bingo' } : undefined,
].filter((x) => x !== undefined);

export const GameTaskAvailabilityOptionsMap = [
	'always',
	appConfig.permissions.allowGps ? 'gps' : undefined,
	'task-completed',
	'task-completed-correct',
]
	.filter((x) => x !== undefined)
	.map((o) => ({ label: o, value: o }));

export const GameMasterValidationOptions = [
	{ label: 'Open', value: 'open' },
	{ label: 'Photo', value: 'photo' },
	{ label: 'Video', value: 'video' },
];

export const AutomaticValidationOptions = [
	{ label: 'Open', value: 'open' },
	{ label: 'Multiple Choice', value: 'multiple-choice' },
	{ label: 'Multi Choice (multiple answers)', value: 'multi-choice' },
];
